import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const EXPIRED_REDIRECT_DELAY_S = 10;

export function formatCountdown(ms: number): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

/**
 * Booking süre geri sayımı — sekme başlığını `deadlineMs`'e göre yönetir, süre dolunca
 * `expired` bayrağını kaldırır ve `EXPIRED_REDIRECT_DELAY_S` sonra ana sayfaya yönlendirir.
 * (Eskiden `AirFlowPage` içindeydi, artık `BookingFlowShell` üzerinden tüm ürünlere açık.)
 */
export function useBookingDeadline({
  deadlineMs,
  paymentDone,
  tabTitlePrefix,
}: {
  deadlineMs: number | null;
  paymentDone: boolean;
  tabTitlePrefix?: string;
}) {
  const navigate = useNavigate();
  const originalTitleRef = useRef(document.title);
  const [remainingMs, setRemainingMs] = useState(0);
  const [expired, setExpired] = useState(false);
  const [redirectIn, setRedirectIn] = useState(EXPIRED_REDIRECT_DELAY_S);

  useEffect(() => {
    setExpired(false);
  }, [deadlineMs]);

  useEffect(() => {
    if (deadlineMs === null || paymentDone) return undefined;
    const tick = () => {
      const remaining = deadlineMs - Date.now();
      setRemainingMs(Math.max(0, remaining));
      if (remaining <= 0) setExpired(true);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [deadlineMs, paymentDone]);

  useEffect(() => {
    if (deadlineMs === null || paymentDone) {
      document.title = originalTitleRef.current;
      return;
    }
    const prefix = tabTitlePrefix ?? 'Rezervasyon';
    document.title = expired ? 'Süreniz Doldu' : `${formatCountdown(remainingMs)} — ${prefix}`;
  }, [remainingMs, deadlineMs, paymentDone, expired, tabTitlePrefix]);

  useEffect(() => {
    if (!expired) {
      setRedirectIn(EXPIRED_REDIRECT_DELAY_S);
      return undefined;
    }
    if (redirectIn <= 0) {
      navigate('/');
      return undefined;
    }
    const timeout = setTimeout(() => setRedirectIn((s) => s - 1), 1000);
    return () => clearTimeout(timeout);
  }, [expired, redirectIn, navigate]);

  useEffect(
    () => () => {
      document.title = originalTitleRef.current;
    },
    []
  );

  return { remainingMs, expired, redirectIn, active: deadlineMs !== null && !paymentDone };
}
